import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API}/api`,
  withCredentials: true, // Send httpOnly auth cookies on every request
});

// Backwards-compatible: if a legacy access_token exists in localStorage, send it once
// (will be cleared after next successful login). New logins use httpOnly cookies only.
api.interceptors.request.use((config) => {
  const legacyToken = localStorage.getItem('access_token');
  if (legacyToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${legacyToken}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/');
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      try {
        // Cookie-based refresh — server reads refresh_token cookie, sets new access_token cookie
        await axios.post(`${API}/api/auth/refresh`, {}, { withCredentials: true });
        return api(originalRequest);
      } catch {
        // Clear any legacy localStorage items and redirect
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('last_activity');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Users
export const getUsers = () => api.get('/users');
export const getUser = (id) => api.get(`/users/${id}`);
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const getUserHierarchy = (id) => api.get(`/users/${id}/hierarchy`);

// Leads
export const getLeads = (params) => api.get('/leads', { params });
export const getLead = (id) => api.get(`/leads/${id}`);
export const createLead = (data) => api.post('/leads', data);
export const updateLead = (id, data) => api.put(`/leads/${id}`, data);
export const deleteLead = (id) => api.delete(`/leads/${id}`);
export const convertLead = (id, data) => api.post(`/leads/${id}/convert`, data);

// Clients
export const getClients = (params) => api.get('/clients', { params });
export const getClient = (id) => api.get(`/clients/${id}`);
export const createClient = (data) => api.post('/clients', data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/clients/${id}`);
export const parseClientScreenshot = (image) => api.post('/clients/parse-screenshot', { image });
export const toggleClientIndividual = (id) => api.put(`/clients/${id}/toggle-individual`);
export const getClientMappedEmployees = (id) => api.get(`/clients/${id}/mapped-employees`);

// Audit Log
export const getAuditLog = (params) => api.get('/audit-log', { params });

// Activities
export const getActivities = (clientId) => api.get('/activities', { params: { client_id: clientId } });
export const createActivity = (data) => api.post('/activities', data);

// Follow-ups
export const getFollowUps = (params) => api.get('/follow-ups', { params });
export const getFollowUp = (id) => api.get(`/follow-ups/${id}`);
export const createFollowUp = (data) => api.post('/follow-ups', data);
export const updateFollowUp = (id, data) => api.put(`/follow-ups/${id}`, data);
export const deleteFollowUp = (id) => api.delete(`/follow-ups/${id}`);

// Tasks
export const getTasks = (params) => api.get('/tasks', { params });
export const getTask = (id) => api.get(`/tasks/${id}`);
export const createTask = (data) => api.post('/tasks', data);
export const createTasksBulk = (tasks) => api.post('/tasks/bulk', tasks);
export const updateTask = (id, data) => api.put(`/tasks/${id}`, data);
export const deleteTask = (id) => api.delete(`/tasks/${id}`);

// Families
export const getFamilies = (params) => api.get('/families', { params });
export const getAllFamilies = () => api.get('/families', { params: { include_details: true } });
export const getFamily = (id) => api.get(`/families/${id}`);
export const createFamily = (data) => api.post('/families', data);
export const updateFamily = (id, data) => api.put(`/families/${id}`, data);
export const deleteFamily = (id) => api.delete(`/families/${id}`);
export const addFamilyMember = (familyId, data) => api.post(`/families/${familyId}/members`, data);
export const removeFamilyMember = (familyId, clientId) => api.delete(`/families/${familyId}/members/${clientId}`);

// Products
export const getProducts = (params) => api.get('/products', { params });
export const getProduct = (id) => api.get(`/products/${id}`);
export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);

// SIPs
export const getSIPs = (params) => api.get('/sips', { params });
export const createSIP = (data) => api.post('/sips', data);
export const updateSIP = (id, data) => api.put(`/sips/${id}`, data);
export const deleteSIP = (id) => api.delete(`/sips/${id}`);

// SIP Reminders
export const getSIPReminders = (params) => api.get('/sip-reminders', { params });
export const createSIPReminder = (data) => api.post('/sip-reminders', data);
export const updateSIPReminder = (id, data) => api.put(`/sip-reminders/${id}`, data);
export const deleteSIPReminder = (id) => api.delete(`/sip-reminders/${id}`);


// Health Insurance
export const getHealthInsurances = (params) => api.get('/health-insurances', { params });
export const createHealthInsurance = (data) => api.post('/health-insurances', data);
export const updateHealthInsurance = (id, data) => api.put(`/health-insurances/${id}`, data);
export const deleteHealthInsurance = (id) => api.delete(`/health-insurances/${id}`);
export const uploadHealthInsuranceDoc = (id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/health-insurances/${id}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Term Insurance
export const getTermInsurances = (params) => api.get('/term-insurances', { params });
export const createTermInsurance = (data) => api.post('/term-insurances', data);
export const updateTermInsurance = (id, data) => api.put(`/term-insurances/${id}`, data);
export const deleteTermInsurance = (id) => api.delete(`/term-insurances/${id}`);
export const uploadTermInsuranceDoc = (id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/term-insurances/${id}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Hierarchy
export const getHierarchyTree = () => api.get('/users/hierarchy-tree');

// CSV Import
export const importLeadsCSV = (file, assignedToUser) => {
  const formData = new FormData();
  formData.append('file', file);
  const params = assignedToUser ? { assigned_to_user: assignedToUser } : {};
  return api.post('/leads/import-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' }, params });
};
export const getLeadsSampleCsvUrl = () => `${API}/api/leads-sample-csv`;

// Image-based lead extraction (e.g., Gymkhana directory pages)
export const parseLeadImages = (files) => {
  const formData = new FormData();
  (files || []).forEach((f) => formData.append('files', f));
  return api.post('/leads/parse-images', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 180000,
  });
};
export const importExtractedLeads = (payload) => api.post('/leads/import-extracted', payload);

// Reports: same-origin URL — browser sends httpOnly cookie on window.open / navigation automatically
export const getReportUrl = (type, format) => `${API}/api/reports/${type}?format=${format}`;
export const downloadReport = async (type, format) => {
  const res = await api.get(`/reports/${type}`, { params: { format }, responseType: 'blob' });
  return res.data;
};

// Client CSV Import
export const importClientsCSV = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/clients/import-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Chat
export const getChatConversations = () => api.get('/chat/conversations');
export const getChatMessages = (partnerId) => api.get(`/chat/messages/${partnerId}`);
export const sendChatMessage = (data) => api.post('/chat/messages', data);
export const getChatUnreadCount = () => api.get('/chat/unread-count');

// Calendar
export const getCalendarEvents = (params) => api.get('/calendar', { params });

// Documents
export const getDocuments = (params) => api.get('/documents', { params });
export const uploadDocument = (clientId, documentType, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/documents/upload?client_id=${clientId}&document_type=${documentType}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const deleteDocument = (id) => api.delete(`/documents/${id}`);
// Documents: cookie-auth download — browser sends httpOnly cookie automatically on same-origin requests
export const getDocumentUrl = (id) => `${API}/api/documents/${id}/download`;

// Notes
export const getNotes = (clientId) => api.get('/notes', { params: { client_id: clientId } });
export const createNote = (data) => api.post('/notes', data);
export const updateNote = (id, data) => api.put(`/notes/${id}`, data);
export const deleteNote = (id) => api.delete(`/notes/${id}`);

// Lead Activities, Notes, Follow-ups, Tasks
export const getLeadActivities = (leadId) => api.get('/lead-activities', { params: { lead_id: leadId } });
export const createLeadActivity = (data) => api.post('/lead-activities', data);
export const getLeadNotes = (leadId) => api.get('/lead-notes', { params: { lead_id: leadId } });
export const createLeadNote = (data) => api.post('/lead-notes', data);
export const updateLeadNote = (id, data) => api.put(`/lead-notes/${id}`, data);
export const getLeadFollowUps = (leadId) => api.get('/lead-follow-ups', { params: { lead_id: leadId } });
export const createLeadFollowUp = (data) => api.post('/lead-follow-ups', data);
export const getLeadTasks = (leadId) => api.get('/lead-tasks', { params: { lead_id: leadId } });
export const createLeadTask = (data) => api.post('/lead-tasks', data);

// Timeline
export const getTimeline = (entityType, entityId) => api.get('/timeline', { params: { entity_type: entityType, entity_id: entityId } });

// Notifications
export const getNotifications = (params) => api.get('/notifications', { params });
export const getUnreadCount = () => api.get('/notifications/unread-count');
export const markNotificationsRead = (ids) => api.post('/notifications/mark-read', { ids });

// Push
export const subscribePush = (subscription) => api.post('/push/subscribe', { subscription });
export const unsubscribePush = () => api.delete('/push/subscribe');
export const getVapidKey = () => api.get('/push/vapid-key');

// Task Attachments
export const uploadTaskAttachment = (taskId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/tasks/${taskId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const downloadTaskAttachment = (taskId, attachmentId) =>
  `${api.defaults.baseURL}/tasks/${taskId}/attachments/${attachmentId}/download`;

// Dashboard
export const getDashboard = () => api.get('/dashboard');

// Search
export const globalSearch = (q) => api.get('/search', { params: { q } });

// Team View
export const getTeamView = (params) => api.get('/team-view', { params });

// Market Crash
export const getMarketCrashList = () => api.get('/market-crash');
export const addMarketCrashEntry = (data) => api.post('/market-crash', data);
export const updateMarketCrashEntry = (id, data) => api.put(`/market-crash/${id}`, data);
export const deleteMarketCrashEntry = (id) => api.delete(`/market-crash/${id}`);

// Incentives
export const getIncentiveConfig = () => api.get('/incentives/config');
export const updateIncentiveConfig = (data) => api.put('/incentives/config', data);
export const getIncentiveTransactions = (params) => api.get('/incentives', { params });
export const createIncentiveTransaction = (data) => api.post('/incentives', data);
export const createIncentiveFromActivity = (data) => api.post('/incentives/from-activity', data);
export const updateIncentiveTransaction = (id, data) => api.put(`/incentives/${id}`, data);
export const deleteIncentiveTransaction = (id) => api.delete(`/incentives/${id}`);
export const splitIncentiveTransaction = (id, splits) => api.post(`/incentives/${id}/split`, { splits });
export const getIncentiveSummary = (params) => api.get('/incentives/summary', { params });


export default api;
